type Hex = string

type Hue        = number
type Light      = number
type Saturation = number

type Red   = number
type Green = number
type Blue  = number

type HLS   = [ Hue, Light, Saturation ]
type RGB   = [ Red, Green, Blue ]

/**
 * Converts hex color to HSL array
 * e.g '#E73DAC' → [ 321, 78, 57 ]
 */
const hexToHslArray = (hex: Hex): HLS => {
  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)

  let r = parseInt(result[1], 16) / 255
  let g = parseInt(result[2], 16) / 255
  let b = parseInt(result[3], 16) / 255

  let max = Math.max(r, g, b)
  let min = Math.min(r, g, b)
  let h: number
  let s: number
  let l: number = (max + min) / 2

  if (max === min) {
    h = 0
    s = 0
  }
  else {
    let d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    // eslint-disable-next-line default-case
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      case b: h = (r - g) / d + 4; break
    }
    h /= 6
  }

  h = Math.round(360 * h)
  s *= 100
  s = Math.round(s)
  l *= 100
  l = Math.round(l)

  return [ h, s, l ]
}

/**
 * Converts hex color to HSL array
 * e.g '#E73DAC' → `hsl(321, 78%, 57%)`
 */
const hexToHslString = (hex: Hex): string => {
  const [ h, s, l ] = hexToHslArray(hex)

  return `hsl(${h}, ${s}%, ${l}%)`
}

/**
 * Converts HSL array to RGB array
 * e.g. [ 321, 78, 57 ] →  [ 231, 60, 171 ]
 */
const hslArrayToRgbArray = ([ h, s, l ]: HLS): RGB => {
  h /= 360
  s /= 100
  l /= 100
  let r: number
  let g: number
  let b: number

  if (s === 0) {
    let v = 255 * l
    r = v
    g = v
    b = v
  }
  else {
    const hueToRgb = (p: number, q: number, t: number) => {
      if (t < 0) {
        t += 1
      }
      if (t > 1) {
        t -= 1
      }
      if (t < 1 / 6) {
        return p + (q - p) * 6 * t
      }
      if (t < 1 / 2) {
        return q
      }
      if (t < 2 / 3) {
        return p + (q - p) * ((2 / 3 - t) * 6)
      }

      return p
    }

    let q = l < 0.5 ? l * (1 + s) : (l + s) - (l * s)
    let p = 2 * l - q

    r = hueToRgb(p, q, h + (1 / 3)) * 255
    g = hueToRgb(p, q, h) * 255
    b = hueToRgb(p, q, h - (1 / 3)) * 255
  }

  return [ Math.round(r), Math.round(g), Math.round(b) ]
}

/**
 * Converts HSL array to RGB array
 * e.g. [ 231, 60, 171 ] →  #E73CAB
 */
const rgbArrayToHex = ([ r, g, b ]: RGB): Hex => {
  const componentToHex = (component: number) => {
    const hex = component.toString(16).toUpperCase()
    return hex.length === 1 ? `0${hex}` : hex
  }

  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`
}

/**
 * Return darker version of color by amount passed
 * e.g #E73DAC, 20 → #A81574
 */
const darker = (hex: Hex, amount: number): Hex => {
  let [ h, l, s ] = hexToHslArray(hex)
  s -= amount
  const [ r, g, b ] = hslArrayToRgbArray([ h, l, s ])
  return rgbArrayToHex([ r, g, b ])
}

/**
 * Return lighter version of color by amount passed
 * e.g #E73DAC, 20 → #F297D2
 */
const lighter = (hex: Hex, amount: number): Hex => {
  let [ h, l, s ] = hexToHslArray(hex)
  s += amount
  const [ r, g, b ] = hslArrayToRgbArray([ h, l, s ])
  return rgbArrayToHex([ r, g, b ])
}

export default {
  hexToHslArray,
  hexToHslString,
  hslArrayToRgbArray,
  rgbArrayToHex,
  darker,
  lighter,
}
